/**
 * The application controller: turns a matched route into a screen.
 *
 *   route change -> guards -> (loading block) -> def.load(ctx) -> def.view(data, ui, ctx) -> patched into #app
 *
 * A route definition looks like:
 *   {
 *     path: '/student/course/:id',
 *     layout: 'app' | 'public' | 'auth',      // chrome around the page
 *     roles: ['student'],                     // omit for public pages
 *     title: 'Course' | (data, ctx) => string,
 *     nav: 'learn',                           // which sidebar item stays highlighted
 *     ui: () => ({ tab: 'all' }),             // fresh per-visit UI state
 *     load: async (ctx) => data,              // API calls; ctx.signal cancels them when you navigate away
 *     view: (data, ui, ctx) => html`...`,     // pure rendering
 *     enter: (ctx) => {},                     // optional: start timers, sockets...
 *     leave: () => {},
 *   }
 * Actions mutate `ui` / `data` through update(), reload() or rerender(); nothing else touches the DOM.
 */
import { bus } from '../core/bus.js';
import { api } from '../core/api.js';
import { patch, recordScroll, stickBottom, $ } from '../core/dom.js';
import { reportError } from '../core/errors.js';
import { register } from '../core/events.js';
import { html, toHtml } from '../core/html.js';
import { navigate, onNavigate, redirect, resolve } from '../core/router.js';
import { homeFor, session } from '../core/session.js';
import { config } from '../core/config.js';
import { errorBlock, loadingBlock } from '../ui/components.js';
import { toast } from '../ui/toast.js';
import { appLayout, publicLayout } from './layouts.js';
import { resolveLink } from './links.js';
import { toggleTheme } from './theme.js';
import { refreshScene } from './scene.js';

const shellUi = { side: false, pop: null, pubMenu: false };
let notifications = [];
let unread = 0;
let view = null;
let navigationId = 0;
let timers = [];
let abort = null;
let announcer = null;

export const currentView = () => view;

/* ---------- guards ---------- */

export function guardFor(def, route, user = session.user) {
  if (!def) return null;
  const needsLogin = def.roles || def.auth === true;
  if (needsLogin && !user) return `/login?next=${encodeURIComponent(route.url)}`;
  if (def.roles && user && !def.roles.includes(user.role)) return homeFor(user.role);
  if (def.guestOnly && user) return homeFor(user.role);
  if (def.guard) return def.guard(user, route) || null;
  return null;
}

/* ---------- rendering ---------- */

function titleOf(def, data, ctx) {
  const t = typeof def.title === 'function' ? def.title(data, ctx) : def.title;
  return t || '';
}

function buildContext(route) {
  return { params: route.params, query: route.query, path: route.path, url: route.url, user: session.user, role: session.role, signal: abort?.signal };
}

function chrome(body) {
  const { def, route } = view;
  const ctx = view.ctx;
  const layout = def.layout || (def.roles ? 'app' : 'public');
  const title = titleOf(def, view.data, ctx);
  const inApp = layout === 'app' && Boolean(session.user);
  document.body.classList.toggle('in-app', inApp);
  document.body.classList.toggle('is-home', !inApp && route.path === '/');
  if (inApp) {
    const role = session.user.role;
    const page = route.path.split('/')[2] || '';
    return appLayout({ role, page, navKey: def.nav, title: title || 'VConnect', body, user: session.user, ui: shellUi, notifications, unread });
  }
  return publicLayout({ path: route.path, body, user: session.user, ui: shellUi, bare: layout === 'auth' });
}

export function rerender() {
  const root = $('#app');
  if (!root || !view) return;
  let body;
  try {
    if (view.error) body = errorBlock(view.error, view.retryable !== false);
    else if (view.loading) body = loadingBlock();
    else body = view.def.view(view.data, view.ui, view.ctx);
  } catch (error) {
    reportError(error, { silent: true });
    console.error(error);
    body = errorBlock(new Error('This screen could not be shown.'), false);
  }
  const scroll = recordScroll(root);
  patch(root, toHtml(chrome(body)));
  stickBottom(root, scroll);
}

function clearTimers() {
  timers.forEach((t) => clearInterval(t));
  timers = [];
}

/** Run fn every `ms` while this screen is open and the tab is visible. Cleared automatically on navigation. */
export function every(ms, fn) {
  const id = setInterval(() => {
    if (document.hidden) return;
    Promise.resolve(fn()).catch((error) => reportError(error, { silent: true }));
  }, ms);
  timers.push(id);
  return id;
}

function setDocumentTitle(def, data, ctx) {
  const t = titleOf(def, data, ctx);
  document.title = t ? `${t} · ${config.brandName}` : `${config.brandName} by Vignova`;
  if (announcer) announcer.textContent = document.title;
}

/** Change per-visit UI state and redraw. */
export function update(fn) {
  if (!view) return;
  if (fn) fn(view.ui, view.data);
  rerender();
}

/** Fetch the page's data again (after a mutation) and redraw, keeping the current screen visible meanwhile. */
export async function reload() {
  if (!view?.def.load) return rerender();
  const mine = view;
  try {
    const data = await mine.def.load(mine.ctx);
    if (view !== mine) return;
    mine.data = data;
    mine.error = null;
  } catch (error) {
    if (view !== mine || error?.name === 'AbortError') return;
    reportError(error);
  }
  rerender();
}

async function show(route, previous) {
  const def = route.def;
  const target = guardFor(def, route);
  if (target) {
    redirect(target);
    return;
  }
  const id = ++navigationId;
  if (view?.def.leave) {
    try {
      view.def.leave(view.ctx);
    } catch (error) {
      reportError(error, { silent: true });
    }
  }
  abort?.abort();
  abort = new AbortController();
  clearTimers();
  shellUi.side = false;
  shellUi.pubMenu = false;
  shellUi.pop = null;

  const effective = def || NOT_FOUND;
  const ctx = buildContext({ ...route, def: effective });
  view = { def: effective, route, ctx, data: null, ui: effective.ui ? effective.ui(ctx) : {}, loading: Boolean(effective.load), error: null };
  setDocumentTitle(effective, null, ctx);
  rerender();
  if (previous && previous.path !== route.path) window.scrollTo(0, 0);

  if (effective.load) {
    try {
      const data = await effective.load(ctx);
      if (id !== navigationId) return;
      view.data = data;
      view.loading = false;
    } catch (error) {
      if (id !== navigationId || error?.name === 'AbortError') return;
      view.loading = false;
      view.error = error;
      reportError(error, { silent: true });
    }
    rerender();
  }
  if (id !== navigationId) return;
  setDocumentTitle(effective, view.data, ctx);
  if (effective.enter && !view.error) {
    try {
      effective.enter(ctx);
    } catch (error) {
      reportError(error, { silent: true });
    }
  }
  if (!previous || previous.path !== route.path) $('#main')?.focus({ preventScroll: true });
}

const NOT_FOUND = {
  path: '*',
  layout: 'public',
  title: 'Page not found',
  view: () => html`<div class="sec"><div class="card empty"><h3>Page not found</h3><p>This link does not exist. It may have moved.</p><a class="btn" href="/">Go home</a></div></div>`,
};

/* ---------- notifications ---------- */

export async function loadNotifications() {
  if (!session.user) {
    notifications = [];
    unread = 0;
    return;
  }
  try {
    const data = await api.get('/notifications', { query: { limit: 15 } });
    notifications = data.notifications;
    unread = data.unread;
    if (view) rerender();
  } catch (error) {
    if (error.status !== 401) reportError(error, { silent: true });
  }
}

/* ---------- actions that belong to the chrome ---------- */

export function installShell() {
  announcer = $('#announcer');
  onNavigate(show);

  bus.on('session:expired', () => {
    session.clear();
    notifications = [];
    unread = 0;
    toast('Your session ended. Please log in again.', 'alert');
    navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
  });
  bus.on('session:changed', () => {
    notifications = [];
    unread = 0;
    if (session.user) loadNotifications();
  });
  setInterval(() => {
    if (!document.hidden && session.user) loadNotifications();
  }, 60_000);

  document.addEventListener('click', (event) => {
    if (shellUi.pop && !event.target.closest('.pop') && !event.target.closest('[data-pop]')) {
      shellUi.pop = null;
      rerender();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && (shellUi.pop || shellUi.side || shellUi.pubMenu)) {
      shellUi.pop = null;
      shellUi.side = false;
      shellUi.pubMenu = false;
      rerender();
    }
  });

  register({
    'ui.openSide': () => { shellUi.side = true; rerender(); },
    'ui.closeSide': () => { shellUi.side = false; rerender(); },
    'ui.pubMenu': () => { shellUi.pubMenu = !shellUi.pubMenu; rerender(); },
    'ui.pop': (el) => {
      shellUi.pop = shellUi.pop === el.dataset.pop ? null : el.dataset.pop;
      rerender();
    },
    'theme.toggle': () => { toggleTheme(); rerender(); refreshScene(); },
    'app.retry': () => (view ? show(view.route, view.route) : undefined),
    'auth.logout': async () => {
      await session.end();
      toast('Logged out', 'out');
      navigate('/');
    },
    'notif.open': async (el) => {
      const link = resolveLink(el.dataset.link, session.role);
      const note = notifications.find((n) => String(n.id) === el.dataset.id);
      shellUi.pop = null;
      if (note && !note.read) {
        note.read = true;
        unread = Math.max(0, unread - 1);
        api.post('/notifications/read', { body: { ids: [note.id] } }).catch(() => {});
      }
      navigate(link);
    },
    'notif.readAll': async () => {
      await api.post('/notifications/read', { body: {} });
      notifications.forEach((n) => { n.read = true; });
      unread = 0;
      rerender();
    },
  });
}

export { resolve as resolveUrl };
